
import React from 'react';
import { Link } from "react-router-dom";
import { NavLink, useLocation } from 'react-router-dom';
import { apiUrl } from '../utils/api';
import { PendingCounts } from '../hooks/usePendingCounts';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  TextField,
  MenuItem,
  Stack,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import LoadingOverlay from './LoadingOverlay';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import ConstructionIcon from '@mui/icons-material/Construction';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ReceiptIcon from '@mui/icons-material/Receipt';
import InventoryIcon from '@mui/icons-material/Inventory';
import FolderIcon from '@mui/icons-material/Folder';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SettingsIcon from '@mui/icons-material/Settings';
import CategoryIcon from '@mui/icons-material/Category';
import BusinessIcon from '@mui/icons-material/Business';
import HistoryIcon from '@mui/icons-material/History';
import ArticleIcon from '@mui/icons-material/Article';
import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';
export const SIDEBAR_WIDTH = 280;

const navIconSx = { color: 'rgba(255, 255, 255, 0.74)' };

// Styling for the top-level "main category" rows so they read as headings
// distinct from sub-page links: heavier weight, slightly larger and tracked.
const mainCategoryTypographyProps = {
  fontWeight: 800 as const,
  fontSize: '0.95rem',
  letterSpacing: 0.3,
};

// Compact, neutral +/- glyph for category expand-collapse. A minus appears
// when the category is open (click to collapse) and a plus when closed (click
// to expand) — more intuitive than chevrons for a list of grouped pages.
const expandIconSx = {
  color: '#fff',
  fontSize: 18,
  border: '1.5px solid rgba(255,255,255,0.55)',
  borderRadius: '4px',
  p: '1px',
};

interface SidebarProps {
  projectId: number | null;
  setProjectId: (id: number | null) => void;
  projects: any[];
  companyId: number | null;
  setCompanyId: (id: number | null) => void;
  companies: any[];
  permissions: Record<string, any>;
  isGlobalAdmin: boolean;
  userContext: any;
  pendingCounts?: PendingCounts;
}

const Sidebar: React.FC<SidebarProps> = ({
  projectId,
  setProjectId,
  projects,
  companyId,
  setCompanyId,
  companies,
  permissions,
  isGlobalAdmin,
  userContext,
  pendingCounts
}) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [designOpen, setDesignOpen] = React.useState(true);
  const [projectsOpen, setProjectsOpen] = React.useState(false);
  const [siteOpen, setSiteOpen] = React.useState(false);
  const [qsOpen, setQsOpen] = React.useState(false);
  const [pmOpen, setPmOpen] = React.useState(false);
  const [aiOpen, setAiOpen] = React.useState(false);
  const [globalSettingsOpen, setGlobalSettingsOpen] = React.useState(false);
  const [companySettingsOpen, setCompanySettingsOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Company options and selection
  const companyOptions = Array.isArray(companies) ? companies : [];
  const selectedCompany = companyOptions.find(
    company => String(company.company_id) === String(companyId)
  );
  const selectedCompanyId = selectedCompany ? String(selectedCompany.company_id) : '';

  // Debug logging
  React.useEffect(() => {
    if (selectedCompany) {
      console.log('[Sidebar] Selected company:', {
        id: selectedCompany.company_id,
        name: selectedCompany.company_name,
        logo_url: selectedCompany.logo_url,
        isGlobalAdmin
      });
    }
  }, [selectedCompany, isGlobalAdmin]);

  // Project options filtered by selected company
  const allProjects = Array.isArray(projects) ? projects : [];
  const projectOptions = companyId
    ? allProjects.filter(project => String(project.company_id) === String(companyId))
    : allProjects; // Show all accessible projects when no company is selected

  const selectedProject = projectOptions.find(
    project => String(project.project_id) === String(projectId)
  );
  const selectedProjectId = selectedProject ? String(selectedProject.project_id) : '';

  const normalizeId = (value: string | number): number | null => {
    if (value === '') {
      return null;
    }
    const numeric = Number(value);
    return Number.isNaN(numeric) ? null : numeric;
  };

  // Helper to get full logo URL
  const getLogoUrl = (logoUrl: string | null | undefined): string => {
    if (!logoUrl) {
      console.log('[Sidebar] No logo URL provided, using default');
      return "/logo/bim-sector-logo-white-300x300.png";
    }
    // If it's a relative path starting with /api/, prepend the API base URL
    if (logoUrl.startsWith('/api/')) {
      const fullUrl = apiUrl(logoUrl);
      console.log('[Sidebar] Converting relative URL:', logoUrl, '→', fullUrl);
      return fullUrl;
    }
    // If it's already a full URL or a public path, return as is
    console.log('[Sidebar] Using URL as-is:', logoUrl);
    return logoUrl;
  };

  // Company formatting and handling
  const formatCompanyLabel = (company: any): string => {
    if (!company) {
      return 'Select Company';
    }
    const parts = [company.company_code, company.company_name].filter(Boolean);
    return parts.join(' - ') || company.company_name || 'Select Company';
  };

  const getCompanyLabel = (value: string | number): string => {
    if (!value) {
      return 'Select Company';
    }
    const match = companyOptions.find(c => String(c.company_id) === String(value));
    return formatCompanyLabel(match);
  };

  const handleCompanyChange = (event: any) => {
    const { value } = event.target;
    const normalizedCompanyId = normalizeId(value as string | number);
    setCompanyId(normalizedCompanyId);

    // Reset project selection when company changes
    if (projectId) {
      const currentProject = allProjects.find(p => String(p.project_id) === String(projectId));
      if (!currentProject || String(currentProject.company_id) !== String(normalizedCompanyId)) {
        setProjectId(null);
      }
    }
  };

  // Project formatting and handling
  const formatProjectLabel = project => {
    if (!project) {
      return 'Select Project';
    }
    const parts = [project.project_number, project.project_name].filter(Boolean);
    return parts.join(' - ') || project.project_name || 'Select Project';
  };

  const getProjectLabel = value => {
    if (!value) {
      return 'Select Project';
    }
    const match = projectOptions.find(p => String(p.project_id) === String(value));
    return formatProjectLabel(match);
  };

  const handleProjectChange = (event: any) => {
    const { value } = event.target;
    const newProjectId = normalizeId(value as string | number);

    if (newProjectId !== projectId) {
      setLoading(true);
      setProjectId(newProjectId);
    }
  };

  React.useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => setLoading(false), 500);
      return () => clearTimeout(timer);
    }
  }, [loading, projectId]);

  const can = page => {
    // Global Admin has access to everything
    if (isGlobalAdmin) {
      return true;
    }

    // All other roles: permission-based access through groups
    const hasPermission = (permissions?.[page] || 'none') !== 'none';
    return hasPermission;
  };

  const canAccessGlobalSettings = (pageName) => {
    // Global admins have access to everything
    if (isGlobalAdmin && ['Companies', 'Projects', 'Groups', 'Users', 'DocumentTemplates', 'AuditLogs'].includes(pageName)) {
      return true;
    }

    // All other roles: permission-based access
    return can(pageName);
  };

  // Show project menus only when a project is selected
  const showProjectMenus = !!projectId;

  // Determine user role
  const userRole = userContext?.highest_role;

  // Show Global Settings for Global Admin only
  const showGlobalSettings = isGlobalAdmin;

  // Check if user has any access (any groups assigned)
  // Global admin always has access, otherwise check if any permission exists
  const hasAnyAccess = isGlobalAdmin || Object.values(permissions || {}).some(perm => perm !== 'none');

  const designPages = [
    { name: 'Dashboard - Design', path: '/', label: 'Dashboard', icon: <DashboardIcon sx={navIconSx} /> },
    { name: 'Design Changes', path: '/design-changes', label: 'Design Changes', icon: <ListAltIcon sx={navIconSx} />, pending: pendingCounts?.dcPending ?? 0, total: pendingCounts?.dcTotal ?? 0 },
    { name: 'Documents', path: '/documents', label: 'Documents', icon: <DescriptionIcon sx={navIconSx} /> },
    { name: 'Document Settings', path: '/document-settings', label: 'Document Settings', icon: <FolderIcon sx={navIconSx} /> },
    { name: 'Assignments', path: '/assignments', label: 'Assignments', icon: <LinkIcon sx={navIconSx} /> }
  ];

  const sitePages = [
    { name: 'Dashboard - Site', path: '/site-dashboard', label: 'Dashboard', icon: <DashboardIcon sx={navIconSx} /> },
    { name: 'Site Tracker', path: '/site-team', label: 'Site Tracker', icon: <ConstructionIcon sx={navIconSx} /> },
    { name: 'Material Requisitions', path: '/material-requisitions', label: 'Material Requisitions', icon: <InventoryIcon sx={navIconSx} />, pending: pendingCounts?.mrPending ?? 0, total: pendingCounts?.siteMrTotal ?? 0 }
  ];

  const qsContractVoPending = (pendingCounts?.qsContractPending ?? 0) + (pendingCounts?.qsVoPending ?? 0);
  const qsContractVoTotal = (pendingCounts?.qsContractTotal ?? 0) + (pendingCounts?.qsVoTotal ?? 0);

  const qsPages = [
    { name: 'Dashboard - QS', path: '/qs-dashboard', label: 'Dashboard', icon: <DashboardIcon sx={navIconSx} /> },
    { name: 'QS Design Change', path: '/qs-team', label: 'QS Design Change', icon: <MonetizationOnIcon sx={navIconSx} />, pending: pendingCounts?.qsDcPending ?? 0, total: pendingCounts?.qsDcTotal ?? 0 },
    { name: 'QS Purchase Orders', path: '/qs-purchase-orders', label: 'QS Purchase Orders', icon: <ReceiptIcon sx={navIconSx} />, pending: pendingCounts?.qsPoPending ?? 0, total: pendingCounts?.qsPoTotal ?? 0 },
    { name: 'QS Contract or VO', path: '/qs-contract-or-vo', label: 'QS Contract & VO', icon: <RequestQuoteIcon sx={navIconSx} />, pending: qsContractVoPending, total: qsContractVoTotal }
  ];

  const projectPages = [
    { name: 'BOQ Categories', path: '/base-scope-categories', label: 'BOQ Categories', icon: <CategoryIcon sx={navIconSx} /> },
    { name: 'BOQ Line Items', path: '/budget-line-items', label: 'BOQ Line Items', icon: <ListAltIcon sx={navIconSx} /> },
    { name: 'Units', path: '/units', label: 'Units', icon: <CategoryIcon sx={navIconSx} /> },
    { name: 'Unit Rate', path: '/unit-rate', label: 'Unit Rate', icon: <MonetizationOnIcon sx={navIconSx} /> }
  ];

  const mrPoPending = (pendingCounts?.mrPending ?? 0) + (pendingCounts?.poPending ?? 0);
  const mrPoTotal = (pendingCounts?.mrTotal ?? 0) + (pendingCounts?.poTotal ?? 0);
  const contractVoPending = (pendingCounts?.contractPending ?? 0) + (pendingCounts?.voPending ?? 0);
  const contractVoTotal = (pendingCounts?.contractTotal ?? 0) + (pendingCounts?.voTotal ?? 0);

 const pmPages = [
    {
      name: 'Dashboard - Design',
      path: '/pm-dashboard',
      label: 'PM Dashboard',
      icon: <DashboardIcon sx={navIconSx} />,
    },
    {
      name: 'MR/PO Approval',
      path: '/mr-po-approval',
      label: 'MR/PO Approval',
      icon: <MonetizationOnIcon sx={navIconSx} />,
      pending: mrPoPending,
      total: mrPoTotal
    },
    {
      name: 'Contract/VO Approval',
      path: '/contract-vo-approval',
      label: 'Contract/VO Approval',
      icon: <RequestQuoteIcon sx={navIconSx} />,
      pending: contractVoPending,
      total: contractVoTotal
    },
  ];

  const aiPages = [
    { name: 'AI Assistant - Chat', path: '/ai-assistant', label: 'Chat', icon: <SmartToyIcon sx={navIconSx} /> },
    { name: 'AI Assistant - Settings', path: '/ai-settings', label: 'Agent Settings', icon: <SettingsIcon sx={navIconSx} /> }
  ];

  // Global Settings - Only for Global Admin (includes everything)
  const globalSettingsPages = [
    { name: 'Companies', path: '/companies', label: 'Companies', icon: <BusinessIcon sx={navIconSx} /> },
    { name: 'Projects', path: '/projects', label: 'Projects', icon: <FolderIcon sx={navIconSx} /> },
    { name: 'Groups', path: '/groups', label: 'Groups', icon: <GroupIcon sx={navIconSx} /> },
    { name: 'Users', path: '/users', label: 'Users', icon: <PersonIcon sx={navIconSx} /> },
    { name: 'DocumentTemplates', path: '/document-templates', label: 'Doc Templates', icon: <ArticleIcon sx={navIconSx} /> },
    { name: 'AuditLogs', path: '/audit-logs', label: 'Audit Logs', icon: <HistoryIcon sx={navIconSx} /> }
  ];

  // Company Settings - For Company/Project Admins (Users, Projects, Groups)
  // Project Admin should NOT see Projects - only Groups and Users
  const isProjectAdmin = userRole === 'Project Admin';
  const companySettingsPages = [
    ...(isProjectAdmin ? [] : [{ name: 'Projects', path: '/projects', label: 'Projects', icon: <FolderIcon sx={navIconSx} /> }]),
    { name: 'Groups', path: '/groups', label: 'Groups', icon: <GroupIcon sx={navIconSx} /> },
    { name: 'Users', path: '/users', label: 'Users', icon: <PersonIcon sx={navIconSx} /> }
  ];

  const isSelected = path => location.pathname === path;

  const renderNavItems = items =>
    items
      .filter(page => can(page.name))
      .map(page => (
        <ListItemButton
          key={page.path}
          component={NavLink}
          to={page.path}
          selected={isSelected(page.path)}
          onClick={isMobile ? handleDrawerToggle : undefined}
          sx={{
            borderRadius: 2,
            mb: 0.5,
            px: 1.5,
            '&.Mui-selected': {
              backgroundColor: 'rgba(255, 255, 255, 0.16)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.20)' }
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>{page.icon}</ListItemIcon>
          <ListItemText
            primary={page.label}
            primaryTypographyProps={{ fontWeight: 600 }}
          />
          {page.total != null && (
            <Box
              component="span"
              sx={{
                ml: 0.5,
                fontSize: '0.72rem',
                fontWeight: 700,
                color: 'rgba(255,255,255,0.75)',
                whiteSpace: 'nowrap',
                backgroundColor: 'rgba(255,255,255,0.10)',
                borderRadius: '10px',
                px: 0.8,
                py: 0.2
              }}
            >
              <Box component="span" sx={{ color: page.pending > 0 ? '#ff6b6b' : 'rgba(255,255,255,0.75)' }}>
                {page.pending}
              </Box>
              /{page.total}
            </Box>
          )}
        </ListItemButton>
      ));

  console.log('[Sidebar] Debug:', {
    companyId,
    isGlobalAdmin,
    companiesCount: companyOptions.length,
    selectedCompany,
    allCompanyIds: companyOptions.map(c => c.company_id)
  });
  const sidebarContent = (
      <Box
        sx={{
          height: '100%',
          background: 'linear-gradient(180deg, #10194e 0%, #111827 100%)',
          color: 'rgba(255, 255, 255, 0.92)',
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid rgba(255, 255, 255, 0.08)',
          position: 'relative'
        }}
      >
        <LoadingOverlay loading={loading} size={50} zIndex={1500} fullScreen={false} />
        <Stack flex={1} spacing={3} sx={{ overflowY: 'auto', px: 3, pb: 3, pt: 3 }}>
          {/* Logo and Company Name - Only show if user has any access */}
          {hasAnyAccess && (
            <Stack spacing={2} alignItems="center">
              {/* Only show logo if company has one */}
              {selectedCompany && selectedCompany.logo_url && (
                <Box
                  component="img"
                  src={getLogoUrl(selectedCompany.logo_url)}
                  alt={`${selectedCompany.company_name} logo`}
                  sx={{
                    width: '100%',
                    borderRadius: 2,
                    objectFit: 'contain',
                    maxHeight: '120px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    padding: 1
                  }}
                  onError={(e: any) => {
                    console.error('[Sidebar] Logo failed to load');
                    // Hide the logo if it fails to load
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <Typography
                variant="h5"
                sx={{ color: '#c9c9c9ff', fontWeight: 700, textAlign: 'center', fontSize: '1.50rem' }}
              >
                {!isGlobalAdmin && selectedCompany ? selectedCompany.company_name : 'Control Centre'}
              </Typography>
            </Stack>
          )}

          {/* Company Selection - Only show if user has access to multiple companies */}
          {hasAnyAccess && companyOptions.length > 1 && (
          <Box>
            <Typography variant="overline" sx={{ color: 'rgba(255, 255, 255, 0.6)', letterSpacing: 0.6, fontSize: '1.1rem' }}>
              Active Company:
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={selectedCompanyId}
              onChange={handleCompanyChange}
              placeholder="Select Company"
              SelectProps={{
                displayEmpty: true,
                renderValue: (value: string | number) => {
                  const label = getCompanyLabel(value);
                  const isPlaceholder = !value || label === 'Select Company';
                  const displayText = isPlaceholder ? 'Select Company' : label;
                  const color = isPlaceholder ? 'rgba(122, 122, 122, 0.6)' : '#020202ff';
                  return (
                    <Typography component="span" sx={{ color, fontStyle: isPlaceholder ? 'italic' : 'normal' }}>
                      {displayText}
                    </Typography>
                  );
                }
              }}
              sx={{
                mt: 1,
                backgroundColor: 'rgba(17, 24, 39, 0.65)',
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.18)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.45)'
                },
                '& .MuiSvgIcon-root': {
                  color: 'rgba(255, 255, 255, 0.7)'
                },
                '& .MuiInputBase-input': {
                  color: '#fff'
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)'
                }
              }}
            >
              <MenuItem value="">
                <em>Select Company</em>
              </MenuItem>
              {companyOptions.map(company => (
                <MenuItem key={company.company_id} value={String(company.company_id)}>
                  {formatCompanyLabel(company)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          )}

          {/* Project Selection */}
          {hasAnyAccess && (
          <Box>
            <Typography variant="overline" sx={{ color: 'rgba(255, 255, 255, 0.6)', letterSpacing: 0.6, fontSize: '1.1rem' }}>
              Active Project:
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={selectedProjectId}
              onChange={handleProjectChange}
              placeholder="Select Project"
              disabled={!companyId && (isGlobalAdmin || companyOptions.length > 1)}
              SelectProps={{
                displayEmpty: true,
                renderValue: value => {
                  const label = getProjectLabel(value);
                  const isPlaceholder = !value || label === 'Select Project';
                  const displayText = isPlaceholder ? 'Select Project' : label;
                  const color = isPlaceholder ? 'rgba(122, 122, 122, 0.6)' : '#020202ff';
                  return (
                    <Typography component="span" sx={{ color, fontStyle: isPlaceholder ? 'italic' : 'normal' }}>
                      {displayText}
                    </Typography>
                  );
                }
              }}
              sx={{
                mt: 1,
                backgroundColor: 'rgba(17, 24, 39, 0.65)',
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.18)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.45)'
                },
                '& .MuiSvgIcon-root': {
                  color: 'rgba(255, 255, 255, 0.7)'
                },
                '& .MuiInputBase-input': {
                  color: '#fff'
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)'
                },
                '&.Mui-disabled': {
                  opacity: 0.6
                }
              }}
            >
              <MenuItem value="">
                <em>Select Project</em>
              </MenuItem>
              {projectOptions.map(project => (
                <MenuItem key={project.project_id} value={String(project.project_id)}>
                  {formatProjectLabel(project)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          )}

          {hasAnyAccess && (
          <>
          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />

          <List
            component="nav"
            subheader={
              showProjectMenus || showGlobalSettings ? (
                <Typography variant="overline" sx={{ color: 'rgba(255, 255, 255, 0.55)', letterSpacing: 0.6 }}>
                  Teams & Modules
                </Typography>
              ) : null
            }
          >
            {showProjectMenus && (
              <>
                {designPages.some(page => can(page.name)) && (
                <Box component="li" sx={{ listStyle: 'none', mb: 1 }}>
                  <ListItemButton onClick={() => setDesignOpen(open => !open)} sx={{ borderRadius: 2, px: 1.5 }}>
                    <ListItemText primary="Design Team" primaryTypographyProps={mainCategoryTypographyProps} />
                    {designOpen ? <Remove sx={expandIconSx} /> : <Add sx={expandIconSx} />}
                  </ListItemButton>
                  <Collapse in={designOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ mt: 0.5 }}>
                      {renderNavItems(designPages)}
                    </List>
                  </Collapse>
                </Box>
              )}

              {sitePages.some(page => can(page.name)) && (
                <Box component="li" sx={{ listStyle: 'none', mb: 1 }}>
                  <ListItemButton onClick={() => setSiteOpen(open => !open)} sx={{ borderRadius: 2, px: 1.5 }}>
                    <ListItemText primary="Site Team" primaryTypographyProps={mainCategoryTypographyProps} />
                    {siteOpen ? <Remove sx={expandIconSx} /> : <Add sx={expandIconSx} />}
                  </ListItemButton>
                  <Collapse in={siteOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ mt: 0.5 }}>
                      {renderNavItems(sitePages)}
                    </List>
                  </Collapse>
                </Box>
              )}

              {qsPages.some(page => can(page.name)) && (
                <Box component="li" sx={{ listStyle: 'none', mb: 1 }}>
                  <ListItemButton onClick={() => setQsOpen(open => !open)} sx={{ borderRadius: 2, px: 1.5 }}>
                    <ListItemText primary="QS Team" primaryTypographyProps={mainCategoryTypographyProps} />
                    {qsOpen ? <Remove sx={expandIconSx} /> : <Add sx={expandIconSx} />}
                  </ListItemButton>
                  <Collapse in={qsOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ mt: 0.5 }}>
                      {renderNavItems(qsPages)}
                    </List>
                  </Collapse>
                </Box>
              )}

              {pmPages.some(page => can(page.name)) && (
                <Box component="li" sx={{ listStyle: 'none', mb: 1 }}>
                  <ListItemButton onClick={() => setPmOpen(open => !open)} sx={{ borderRadius: 2, px: 1.5 }}>
                    <ListItemText primary="Project Management" primaryTypographyProps={mainCategoryTypographyProps} />
                    {pmOpen ? <Remove sx={expandIconSx} /> : <Add sx={expandIconSx} />}
                  </ListItemButton>
                  <Collapse in={pmOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ mt: 0.5 }}>
                      {renderNavItems(pmPages)}
                    </List>
                  </Collapse>
                </Box>
              )}

              {aiPages.some(page => can(page.name)) && (
                <Box component="li" sx={{ listStyle: 'none', mb: 1 }}>
                  <ListItemButton onClick={() => setAiOpen(open => !open)} sx={{ borderRadius: 2, px: 1.5 }}>
                    <ListItemText primary="AI Assistant" primaryTypographyProps={mainCategoryTypographyProps} />
                    {aiOpen ? <Remove sx={expandIconSx} /> : <Add sx={expandIconSx} />}
                  </ListItemButton>
                  <Collapse in={aiOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ mt: 0.5 }}>
                      {renderNavItems(aiPages)}
                    </List>
                  </Collapse>
                </Box>
              )}

              {projectPages.some(page => can(page.name)) && (
                <Box component="li" sx={{ listStyle: 'none', mb: 1 }}>
                  <ListItemButton onClick={() => setProjectsOpen(open => !open)} sx={{ borderRadius: 2, px: 1.5 }}>
                    <ListItemText primary="Project Settings" primaryTypographyProps={mainCategoryTypographyProps} />
                    {projectsOpen ? <Remove sx={expandIconSx} /> : <Add sx={expandIconSx} />}
                  </ListItemButton>
                  <Collapse in={projectsOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ mt: 0.5 }}>
                      {renderNavItems(projectPages)}
                    </List>
                  </Collapse>
                </Box>
              )}
              </>
            )}

            {/* Global Settings - Only for Global Admin */}
            {showGlobalSettings && globalSettingsPages.some(page => canAccessGlobalSettings(page.name)) && (
              <Box component="li" sx={{ listStyle: 'none', mb: 1 }}>
                <ListItemButton onClick={() => setGlobalSettingsOpen(open => !open)} sx={{ borderRadius: 2, px: 1.5 }}>
                  <ListItemText primary="Global Settings" primaryTypographyProps={mainCategoryTypographyProps} />
                  {globalSettingsOpen ? <Remove sx={expandIconSx} /> : <Add sx={expandIconSx} />}
                </ListItemButton>
                <Collapse in={globalSettingsOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ mt: 0.5 }}>
                    {globalSettingsPages
                      .filter(page => canAccessGlobalSettings(page.name))
                      .map(page => (
                        <ListItemButton
                          key={page.path}
                          component={NavLink}
                          to={page.path}
                          selected={isSelected(page.path)}
                          onClick={isMobile ? handleDrawerToggle : undefined}
                          sx={{
                            borderRadius: 2,
                            mb: 0.5,
                            px: 1.5,
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(255, 255, 255, 0.16)',
                              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.20)' }
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>{page.icon}</ListItemIcon>
                          <ListItemText primary={page.label} primaryTypographyProps={{ fontWeight: 600 }} />
                        </ListItemButton>
                      ))}
                  </List>
                </Collapse>
              </Box>
            )}

            {/* Settings - For Company/Project Admin only (NOT Global Admin) */}
            {!isGlobalAdmin && companySettingsPages.filter(page => can(page.name)).length > 0 && (
              <Box component="li" sx={{ listStyle: 'none', mb: 1 }}>
                <ListItemButton onClick={() => setCompanySettingsOpen(open => !open)} sx={{ borderRadius: 2, px: 1.5 }}>
                  <ListItemText primary="Settings" primaryTypographyProps={mainCategoryTypographyProps} />
                  {companySettingsOpen ? <Remove sx={expandIconSx} /> : <Add sx={expandIconSx} />}
                </ListItemButton>
                <Collapse in={companySettingsOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ mt: 0.5 }}>
                    {companySettingsPages
                      .filter(page => can(page.name))
                      .map(page => (
                      <ListItemButton
                        key={page.path}
                        component={NavLink}
                        to={page.path}
                        selected={isSelected(page.path)}
                        onClick={isMobile ? handleDrawerToggle : undefined}
                        sx={{
                          borderRadius: 2,
                          mb: 0.5,
                          px: 1.5,
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.16)',
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.20)' }
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>{page.icon}</ListItemIcon>
                        <ListItemText primary={page.label} primaryTypographyProps={{ fontWeight: 600 }} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </Box>
            )}
          </List>
          </>
          )}

          {/* Show message when user has no access */}
          {!hasAnyAccess && (
            <Box sx={{
              textAlign: 'center',
              mt: 4,
              px: 2,
              py: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 2
            }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                No Access
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                You are not assigned to any groups
              </Typography>
            </Box>
          )}
        </Stack>

        <Box sx={{ px: 3, pb: 3 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.35)' }}>
            Copyright {new Date().getFullYear()} BIM Sector
          </Typography>
        </Box>
      </Box>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      {isMobile && (
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: theme => theme.zIndex.drawer + 2,
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark'
            },
            boxShadow: 3
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Mobile Drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: SIDEBAR_WIDTH
            }
          }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        /* Desktop Sidebar */
        <Box
          component="aside"
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0
          }}
        >
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: theme => theme.zIndex.drawer,
              width: SIDEBAR_WIDTH
            }}
          >
            {sidebarContent}
          </Box>
        </Box>
      )}
    </>
  );
};

export default Sidebar;
